/**
 * 管理端 AntD 主题 token + 设计系统常量。
 *
 * 继承原型视觉世界（教育青 teal #0f6b5c 系主色 + 暖橙 #e8791a 行动强调色），
 * 补全原型 tokens.css 的完整语义层：中性色 / 状态色 / 字体栈 / 三档圆角阴影。
 * 色值经主题 token 与 CSS 变量（styles/tokens.css）双轨同值统一，避免组件内 raw hex 散落。
 * Operate Mode：管理后台重任务/信息/状态，反装饰；层级靠对比，不堆砌效果。
 *
 * craft floor 落点：
 * - 侧栏深色背景的二级文字取青绿系浅色（never pure gray），与品牌色系协调；
 * - 阴影同时有偏移与柔焦（非零偏移彩色光晕）；
 * - 字体用苹果方/微软雅黑栈，中文渲染更清爽；
 * - 状态色比 AntD 默认更沉稳，对齐教育/专业语调。
 */
import type { ThemeConfig } from 'antd';

/** 品牌主色（深青，对齐原型 #0f6b5c） */
export const BRAND = '#0f6b5c';
/** 品牌主色 hover（对齐原型 #0c5548，更深青） */
export const BRAND_HOVER = '#0c5548';
/** 品牌浅背景（对齐原型 #e3f2ef） */
export const BRAND_BG = '#e3f2ef';

/** 行动强调色（暖橙，仅用于报名/缴费/试课等主要操作，对齐原型 #e8791a） */
export const ACCENT = '#e8791a';
/** 强调色 hover */
export const ACCENT_HOVER = '#cf6910';
/** 强调色浅背景 */
export const ACCENT_BG = '#fdf0e2';

/** 字体栈（首位思源黑体 Noto Sans SC，对齐 Playwright 渲染基准；回退苹方/微软雅黑，中文渲染更清爽） */
export const FONT_FAMILY =
  '"Noto Sans SC", -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", "Segoe UI", Roboto, sans-serif';

/** 卡片阴影（对齐原型：偏移 2px + 柔焦 10px，craft floor 要求阴影同时有偏移与柔焦） */
export const SHADOW_CARD = '0 2px 10px rgba(15, 32, 28, 0.06)';
/** 浮层阴影（Dropdown/登录卡/Modal 等，偏移 8px + 柔焦 28px） */
export const SHADOW_POPOVER = '0 8px 28px rgba(15, 32, 28, 0.14)';

/** 侧栏深色背景（青绿系深色，非 AntD 默认纯黑 #001529，对齐原型 #102a25） */
const SIDER_BG = '#102a25';
/** 侧栏子菜单更深底（青绿系，非 #000c17 纯黑） */
const SIDER_BG_DEEP = '#0a1f1a';
/** 侧栏未选中文字（青绿系浅色，craft floor：彩色表面二级文字 never pure gray） */
const SIDER_TEXT = '#b8c5c0';

/** 管理端亮色主题 */
export const adminTheme: ThemeConfig = {
  token: {
    colorPrimary: BRAND,
    colorInfo: BRAND,
    colorLink: BRAND,
    colorSuccess: '#1f9254',
    colorWarning: '#b8860b',
    colorError: '#c62828',
    borderRadius: 8,
    fontFamily: FONT_FAMILY,
    colorText: '#1f2733',
    colorTextSecondary: '#5b6472',
    colorTextPlaceholder: '#98a1ad',
    colorBorder: '#e2e6ea',
    colorBgLayout: '#f4f6f7',
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      headerHeight: 56,
      headerPadding: '0 20px',
      siderBg: SIDER_BG,
      bodyBg: '#f4f6f7',
    },
    Menu: {
      // 侧栏深色青绿系（替换 AntD 默认纯黑 #001529）
      darkItemBg: SIDER_BG,
      darkSubMenuItemBg: SIDER_BG_DEEP,
      darkItemSelectedBg: BRAND,
      darkItemColor: SIDER_TEXT,
      darkItemSelectedColor: '#ffffff',
      darkItemHoverColor: '#ffffff',
      darkItemHoverBg: 'rgba(15, 107, 92, 0.18)',
      // itemHeight / iconSize 提升视觉舒展度，解决「导航栏偏小」
      itemHeight: 44,
      iconSize: 16,
      fontSize: 14,
    },
    Card: {
      boxShadowTertiary: SHADOW_CARD,
    },
    Button: {
      primaryShadow: 'none',
      defaultShadow: 'none',
    },
  },
};
